// Quick Create character defaults.
//
// Defaults are layered:
//   1. CLASS_DEFAULTS — the primary source. Every class must define a complete
//      set of class-level picks (skills, equipmentPackage, featureChoices, and
//      preparedSpells for casters).
//   2. SPECIES_DEFAULTS — per-species picks (lineage, trait choices, etc.).
//      Empty for species with no creation-time choices.
//   3. BACKGROUND_DEFAULTS — per-background picks (tool proficiency, equipment
//      package, feat choices).
//   4. COMBO_OVERRIDES — shallow per-field overrides keyed by
//      "{class}/{species}/{background}". Each present field wholly replaces the
//      base layer's value for that field.
//
// The resolver produces a full POST /api/characters payload when combined with
// name + token. Defaults intentionally live here (not in SRD data) because
// quick create is allowed to make opinionated picks that differ from SRD
// "recommended" values.

/**
 * @typedef {{
 *   skills: string[],
 *   equipmentPackage: string,
 *   featureChoices: {[key: string]: string[]},
 *   preparedSpells?: string[],
 * }} ClassDefaults
 */

/**
 * @typedef {{
 *   size?: string,
 *   lineage?: string,
 *   traitChoices?: {[key: string]: string | string[]},
 *   lineageChoices?: {[key: string]: string | string[]},
 *   originFeatChoices?: {[key: string]: string | string[]},
 * }} SpeciesDefaults
 */

/**
 * @typedef {{
 *   toolProficiency?: string,
 *   equipmentPackage?: string,
 *   featChoices?: {[key: string]: string | string[]},
 * }} BackgroundDefaults
 */

/**
 * @typedef {{
 *   class?: Partial<ClassDefaults>,
 *   species?: Partial<SpeciesDefaults>,
 *   background?: Partial<BackgroundDefaults>,
 * }} ComboOverride
 */

/** @type {{[key: string]: ClassDefaults}} */
export const CLASS_DEFAULTS = {
  cleric: {
    skills: ['medicine', 'religion'],
    equipmentPackage: 'A',
    featureChoices: {
      'divine-order': ['protector'],
      cantrips: ['guidance', 'sacred-flame', 'resistance'],
    },
    preparedSpells: ['bless', 'cure-wounds', 'guiding-bolt', 'shield-of-faith'],
  },
  fighter: {
    skills: ['athletics', 'intimidation'],
    equipmentPackage: 'A',
    featureChoices: {
      'fighting-style': ['defense'],
      'weapon-mastery': ['greatsword', 'flail', 'javelin'],
    },
  },
  rogue: {
    skills: ['sleight-of-hand', 'stealth', 'perception', 'acrobatics'],
    equipmentPackage: 'A',
    featureChoices: {
      'expertise-1': ['sleight-of-hand', 'stealth'],
      'weapon-mastery': ['dagger', 'shortsword'],
      'thieves-cant': ['elvish'],
    },
  },
  wizard: {
    skills: ['arcana', 'investigation'],
    equipmentPackage: 'A',
    featureChoices: {
      cantrips: ['fire-bolt', 'ray-of-frost', 'light'],
      spellbook: ['magic-missile', 'chromatic-orb', 'thunderwave', 'mage-armor', 'sleep', 'burning-hands'],
    },
    // Pre-prepare a balanced set from the spellbook so the prep-phase
    // picker opens with what the player actually starts with rather than
    // 0/N. Picked manually to a level-1 wizard's count (preparedSpellsByLevel[0] = 4).
    preparedSpells: ['magic-missile', 'mage-armor', 'sleep', 'burning-hands'],
  },
};

/** @type {{[key: string]: SpeciesDefaults}} */
export const SPECIES_DEFAULTS = {
  dwarf: {},
  elf: {
    lineage: 'high-elf',
    lineageChoices: { spellcastingAbility: 'int', cantrip: 'light' },
    traitChoices: { 'keen-senses': 'perception' },
  },
  halfling: {},
  human: {
    size: 'medium',
    traitChoices: { skillful: 'perception', originFeat: 'skilled' },
  },
};

/** @type {{[key: string]: BackgroundDefaults}} */
export const BACKGROUND_DEFAULTS = {
  // Acolyte's origin feat is Magic Initiate, locked to the cleric spell list
  // by the background data itself. Quick create fills in the remaining
  // Magic Initiate choices: WIS as the spellcasting ability (matches Cleric),
  // two cantrips that don't duplicate the cleric class default cantrips
  // (light, spare-the-dying), and Healing Word as the once-per-long-rest L1.
  acolyte: {
    equipmentPackage: 'A',
    featChoices: {
      spellcastingAbility: 'wis',
      cantrips: ['light', 'spare-the-dying'],
      spell: 'healing-word',
    },
  },
  criminal: { equipmentPackage: 'A' },
  // Sage's origin feat is Magic Initiate, locked to the wizard spell list
  // by the background data itself. Quick create fills in the remaining
  // Magic Initiate choices for a cleric pickup: WIS as the spellcasting
  // ability (matches Cleric), two offensive cantrips off the wizard list
  // (light, fire-bolt), and Magic Missile as the once-per-long-rest L1.
  sage: {
    equipmentPackage: 'A',
    featChoices: {
      spellcastingAbility: 'wis',
      cantrips: ['light', 'fire-bolt'],
      spell: 'magic-missile',
    },
  },
  soldier: { equipmentPackage: 'A', toolProficiency: 'dice-set' },
};

/**
 * Shallow per-field overrides keyed by "{class}/{species}/{background}".
 * Populate this when a specific combination needs to differ from the base
 * class/species/background defaults. Empty by default — most combos rely
 * entirely on the base layers.
 *
 * @type {{[key: string]: ComboOverride}}
 */
export const COMBO_OVERRIDES = {};

/**
 * Merge the base defaults for a given class/species/background with any
 * combo-specific overrides. Shallow per-field: each field named on the
 * override wholly replaces the base layer's value for that field.
 *
 * @param {{classSlug: string, speciesSlug: string, backgroundSlug: string}} args
 */
export function resolveQuickCreateDefaults({ classSlug, speciesSlug, backgroundSlug }) {
  const classBase = CLASS_DEFAULTS[classSlug];
  const speciesBase = SPECIES_DEFAULTS[speciesSlug];
  const backgroundBase = BACKGROUND_DEFAULTS[backgroundSlug];
  if (!classBase) throw new Error(`quick-create: unknown class "${classSlug}"`);
  if (!speciesBase) throw new Error(`quick-create: unknown species "${speciesSlug}"`);
  if (!backgroundBase) throw new Error(`quick-create: unknown background "${backgroundSlug}"`);

  const override = COMBO_OVERRIDES[`${classSlug}/${speciesSlug}/${backgroundSlug}`] || {};
  return {
    class: { ...classBase, ...(override.class || {}) },
    species: { ...speciesBase, ...(override.species || {}) },
    background: { ...backgroundBase, ...(override.background || {}) },
  };
}

/**
 * Build a complete POST /api/characters request body from the user's five
 * quick-create inputs plus the resolved defaults. Mirrors the field shape
 * produced by create-character-view.js `_createCharacter` so the backend
 * cannot tell a quick-created character apart from a custom-created one.
 *
 * @param {{
 *   classSlug: string,
 *   speciesSlug: string,
 *   backgroundSlug: string,
 *   name: string,
 *   tokenSlug: string,
 * }} args
 */
export function buildQuickCreateRequest({ classSlug, speciesSlug, backgroundSlug, name, tokenSlug }) {
  const resolved = resolveQuickCreateDefaults({ classSlug, speciesSlug, backgroundSlug });
  const classDefaults = resolved.class;
  const speciesDefaults = resolved.species;
  const backgroundDefaults = resolved.background;

  return {
    name,
    class: classSlug,
    species: speciesSlug,
    background: backgroundSlug,
    token: tokenSlug,
    classChoices: {
      skills: classDefaults.skills,
      equipmentPackage: classDefaults.equipmentPackage,
      featureChoices: classDefaults.featureChoices,
      ...(classDefaults.preparedSpells ? { preparedSpells: classDefaults.preparedSpells } : {}),
    },
    speciesChoices: {
      size: speciesDefaults.size || '',
      lineage: speciesDefaults.lineage || '',
      traitChoices: speciesDefaults.traitChoices || {},
      lineageChoices: speciesDefaults.lineageChoices || {},
      originFeatChoices: speciesDefaults.originFeatChoices || {},
    },
    backgroundChoices: {
      toolProficiency: backgroundDefaults.toolProficiency || '',
      equipmentPackage: backgroundDefaults.equipmentPackage || '',
      featChoices: backgroundDefaults.featChoices || {},
    },
  };
}
